<template>
  <div ref="filterRef" :class="$style.root">
    <div :class="$style.quick_filter">
      <AtomsConsumerButton :class="$style.filter_button" @click="$emit('update:openFilter', true)">
        <AtomImage src="/images/icon/filter-icon.svg" alt="filter" :width="16" :height="16" />
        絞り込み{{ filterCountNum ? `(${filterCountNum})` : "" }}
      </AtomsConsumerButton>
      <div :class="$style.scroll_area">
        <div :class="$style.filter_list">
          <AtomsConsumerButton variant="secondary" @click="() => toggleCategoryAccordion('sex')">
            性別
            <AtomsConsumerImage src="/images/icon/arrow-down.svg" alt="arrow_down" :width="12" :height="6" />
          </AtomsConsumerButton>
          <AtomsConsumerButton variant="secondary" @click="() => toggleCategoryAccordion('wearCategory')">
            セット
            <AtomsConsumerImage src="/images/icon/arrow-down.svg" alt="arrow_down" :width="12" :height="6" />
          </AtomsConsumerButton>
          <AtomsConsumerButton variant="secondary" @click="() => toggleCategoryAccordion('size')">
            サイズ
            <AtomsConsumerImage src="/images/icon/arrow-down.svg" alt="arrow_down" :width="12" :height="6" />
          </AtomsConsumerButton>
          <AtomsConsumerButton variant="secondary" @click="() => toggleCategoryAccordion('season')">
            シーズン
            <AtomsConsumerImage src="/images/icon/arrow-down.svg" alt="arrow_down" :width="12" :height="6" />
          </AtomsConsumerButton>
          <AtomsConsumerButton variant="secondary" @click="() => toggleCategoryAccordion('fabric')">
            素材
            <AtomsConsumerImage src="/images/icon/arrow-down.svg" alt="arrow_down" :width="12" :height="6" />
          </AtomsConsumerButton>
          <AtomsConsumerButton variant="secondary" @click="() => toggleCategoryAccordion('category')">
            カテゴリー
            <AtomsConsumerImage src="/images/icon/arrow-down.svg" alt="arrow_down" :width="12" :height="6" />
          </AtomsConsumerButton>
        </div>
      </div>
    </div>
    <div v-show="isOpenFilter && filterCategory" :class="$style.category_accordion">
      <AtomsConsumerButton :class="$style.close" @click="toggleCategoryAccordion()" />
      <div v-show="filterCategory === 'sex'">
        <p>性別</p>
        <div :class="$style.checkbox_list">
          <CommonMultiCheckBox
            v-model:value="localFilter.sex"
            name="sex"
            :list="sexItems"
            :is-required-text-form="false"
          />
        </div>
      </div>

      <div v-show="filterCategory === 'wearCategory'">
        <p>セット</p>
        <div :class="$style.checkbox_list">
          <CommonMultiCheckBox
            v-model:value="localFilter.wear_category"
            name="wearCategory"
            :list="wearCategoryItems"
            :is-required-text-form="false"
          />
        </div>
      </div>

      <div v-show="filterCategory === 'size'">
        <p>サイズ</p>
        <div :class="$style.checkbox_list">
          <CommonMultiCheckBox
            v-model:value="localFilter.size"
            name="size"
            :list="sizeItems"
            :is-required-text-form="false"
          />
        </div>
      </div>

      <div v-show="filterCategory === 'season'">
        <p>シーズン</p>
        <div :class="$style.checkbox_list">
          <CommonMultiCheckBox
            v-model:value="localFilter.season"
            name="season"
            :list="seasonItems"
            :is-required-text-form="false"
          />
        </div>
      </div>

      <div v-show="filterCategory === 'fabric'">
        <p>素材</p>
        <div :class="$style.checkbox_list">
          <CommonMultiCheckBox
            v-model:value="localFilter.fabric"
            name="fabric"
            :list="fabricItems"
            :is-required-text-form="false"
          />
        </div>
      </div>

      <div v-show="filterCategory === 'category'">
        <div v-if="isLinkCategory && categories">
          <p>サブカテゴリー</p>
          <div :class="$style.checkbox_list">
            <AtomsConsumerLink
              v-for="category in categories"
              :key="category.slug"
              :url="`/${categorySlug}/${category.slug}`"
              :disabled="checkCurrentPage(category.slug)"
              :class="{ [$style.active]: checkCurrentPage(category.slug) }"
            >
              {{ category.name }}
            </AtomsConsumerLink>
          </div>
        </div>
        <div v-else>
          <p>カテゴリー</p>
          <div :class="$style.checkbox_list">
            <AtomsConsumerRadioButton
              v-for="category in allListCategories"
              :key="category.document_id"
              :checked="localFilter.category ?? ''"
              name="category"
              :value="category.document_id || ''"
              :class="[$style.item, { [$style.active]: localFilter.category === category.document_id }]"
              @change="(v) => (localFilter.category = v)"
            >
              {{ category.name }}
            </AtomsConsumerRadioButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { cloneDeep } from "~/utils/functions/common"
import { FABRIC_ITEMS, SEASON_ITEMS, SEX_ITEMS, SIZE_ITEMS, WEAR_CATEGORY_ITEMS } from "~/const/product"
import type { ProductQuickFilterV2Props } from "~/types/product"

const props = defineProps<ProductQuickFilterV2Props>()

const emit = defineEmits<{
  (e: "update:filter", value: ProductQuickFilterV2Props["filter"]): void
  (e: "reset"): void
  (e: "update:openFilter", isOpenFilterSp: boolean): void
}>()

const { buildFilterFromQuery } = useProductsPage()
const localFilter = ref(buildFilterFromQuery(props.filter))

const { y } = useWindowScroll()
const { width: windowWidth } = useWindowSize()
const md = computed(() => windowWidth.value >= 768)
const isOpenFilter = ref(false)
const filterCategory = ref("")
const filterRef = ref<HTMLElement>()
const route = useRoute()
const categorySlug = route.params.categorySlug

const toggleCategoryAccordion = (category?: string) => {
  const _category = cloneDeep(filterCategory.value)
  if (category) filterCategory.value = category
  isOpenFilter.value =
    filterCategory.value === _category || !isOpenFilter.value ? !isOpenFilter.value : isOpenFilter.value
}

type CommonMultiCheckBoxItem = {
  key: string
  value: string
  disabled?: boolean
}

const filterCountNum = computed(() => {
  return Object.values(localFilter.value).reduce((acc, cur) => {
    if (Array.isArray(cur)) {
      return acc + cur.length
    }
    if (typeof cur === "boolean") {
      return acc + (cur ? 1 : 0)
    }
    return acc
  }, 0)
})

const sexItems = computed<CommonMultiCheckBoxItem[]>(() => {
  return SEX_ITEMS.map((item) => ({
    ...item,
    disabled: !props.filterCounts[`sex_${item.value}`],
  }))
})
const fabricItems = computed<CommonMultiCheckBoxItem[]>(() => {
  return FABRIC_ITEMS.map((item) => ({
    ...item,
    disabled: !props.filterCounts[`fabric_${item.value}`],
  }))
})
const seasonItems = computed<CommonMultiCheckBoxItem[]>(() => {
  return SEASON_ITEMS.map((item) => ({
    ...item,
    disabled: !props.filterCounts[`season_${item.value}`],
  }))
})
const wearCategoryItems = computed<CommonMultiCheckBoxItem[]>(() => {
  return WEAR_CATEGORY_ITEMS.map((item) => ({
    ...item,
    disabled: !props.filterCounts[`wear_category_${item.value}`],
  }))
})
const sizeItems = computed<CommonMultiCheckBoxItem[]>(() => {
  return SIZE_ITEMS.map((item) => ({
    ...item,
    disabled: item.value !== "その他" ? !props.filterCounts[`size_${item.value}`] : !props.filterCounts.size_other,
  }))
})

const setFilterPosition = (): void => {
  const filterElement = filterRef.value
  const headerElement = document.getElementById("site-header-id")
  if (!filterElement || !headerElement) return
  const headerHeight = headerElement.clientHeight
  headerElement.classList.contains("hide")
    ? (filterElement.style.top = "0")
    : (filterElement.style.top = headerHeight + "px")
}

const checkCurrentPage = (urlKey: string): boolean => {
  const { path } = route
  const splitPathArray = path.split("/")
  if (!splitPathArray || !splitPathArray[2]) return false
  if (path.match(urlKey)) return true

  return false
}

watch(
  () => props.filter,
  (newValue) => {
    localFilter.value = buildFilterFromQuery(newValue)
  },
  { deep: true },
)

watch(
  () => localFilter.value,
  (newValue) => {
    if (JSON.stringify(buildFilterFromQuery(props.filter)) === JSON.stringify(localFilter.value)) return
    emit("update:filter", newValue)
  },
  { deep: true },
)

watch(y, () => {
  if (md.value) return
  setFilterPosition()
})
</script>

<style lang="scss" module>
.root {
  position: sticky;
  top: 0;
  z-index: 3;
  transition: 0.4s ease-in-out;
  background: rgba($white, 0.9);
  backdrop-filter: blur(10px);
  .filter_button {
    @include sub-font;
    text-decoration: underline;
  }
  .quick_filter {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.5rem 0 0.5rem 0.5rem;
    white-space: nowrap;
    border-bottom: 1px solid $primary-20;
    .scroll_area {
      overflow-x: auto;
      .filter_list {
        display: flex;
        gap: 0.5rem;
        white-space: nowrap;
        :global(button) {
          @include sub-font;
          color: $text-black;
          font-size: $font-size-12;
          padding: 0.375rem 0.875rem !important;
          border-radius: 1.5rem !important;
          &:focus {
            box-shadow: none;
          }
        }
      }
    }
  }
  .category_accordion {
    position: relative;
    padding: 1rem;
    border-bottom: 1px solid $primary-20;
    p {
      font-size: $font-size-16;
      margin-bottom: 0.5rem;
    }
    .close {
      position: absolute;
      top: 1rem;
      right: 1rem;
      padding: 15px;
      cursor: pointer;
      &::before,
      &::after {
        position: absolute;
        top: 14px;
        left: 0px;
        width: 1.5rem;
        height: 1px;
        content: "";
        background: $primary;
      }
      &::before {
        transform: rotate(45deg);
      }
      &::after {
        transform: rotate(-45deg);
      }
    }
    .checkbox_list {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      .active {
        color: $primary;
        font-weight: bold;
        pointer-events: none;
      }
    }
  }
}
</style>
